import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';

/**
 * Measures its host element's border-box height and keeps `height` in sync
 * via `ResizeObserver`, so a sibling element can expose it as a CSS custom
 * property (e.g. `[style.--payments-header-height]="directive.height() + 'px'"`)
 * for a sticky element to offset itself by. Read the signal from the
 * component via `viewChild(StickyHeaderHeightDirective)` — a component-level
 * query resolves across `@if`/`@for` blocks the way a template-local `#ref`
 * variable can't, which matters here since the header is often behind an
 * `@if`.
 *
 * Previously duplicated (host `ElementRef` query + `effect()` +
 * `ResizeObserver` + cleanup) verbatim in both payment modals.
 */
@Directive({
  selector: '[appStickyHeaderHeight]',
  standalone: true,
})
export class StickyHeaderHeightDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private resizeObserver?: ResizeObserver;

  readonly height = signal<number>(0);

  ngAfterViewInit(): void {
    const element = this.elementRef.nativeElement;
    this.height.set(element.offsetHeight);

    this.resizeObserver = new ResizeObserver(([entry]) => {
      const borderBoxSize = Array.isArray(entry.borderBoxSize)
        ? entry.borderBoxSize[0]
        : entry.borderBoxSize;
      this.height.set(borderBoxSize?.blockSize ?? entry.contentRect.height);
    });
    this.resizeObserver.observe(element, { box: 'border-box' });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }
}
